import React, { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import Modal from '../Modal/Modal';
import IconX from '@pano-project/assets/icons/IconX';
import { useBottomSheetDrag } from '../../hooks/useBottomSheetDrag';
import './_BottomSheetModal.sass';

export interface BottomSheetModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: ReactNode;
  children: ReactNode;
  onGrabberClick?: () => void;
  closeIcon?: boolean;
  onCloseIcon?: () => void;
  disableBackdropClose?: boolean;
  height?: string;
  minHeight?: string;
  maxHeight?: string;
  bodyMaxHeight?: string;
  className?: string;
  headerContent?: ReactNode;
  backdropEnabled?: boolean;
  disableOpenAnimation?: boolean;
}

const BottomSheetModal: React.FC<BottomSheetModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  onGrabberClick,
  closeIcon = true,
  onCloseIcon,
  disableBackdropClose = false,
  height,
  minHeight,
  maxHeight,
  bodyMaxHeight,
  className = '',
  headerContent,
  backdropEnabled = true,
  disableOpenAnimation = false,
}) => {
  const [isClosing, setIsClosing] = useState(false);
  const [shouldRender, setShouldRender] = useState(isOpen);
  const [isVisible, setIsVisible] = useState(false);
  const closeTimerRef = useRef<NodeJS.Timeout | null>(null);
  const openRafRef = useRef<number | null>(null);

  const triggerClose = useCallback(
    (callback?: () => void) => {
      if (!isClosing) {
        setIsClosing(true);
        setIsVisible(false);
        closeTimerRef.current = setTimeout(() => {
          setShouldRender(false);
          setIsClosing(false);
          if (callback) callback();
          else onClose();
        }, 150);
      }
    },
    [isClosing, onClose],
  );

  const handleClose = useCallback(() => {
    triggerClose();
  }, [triggerClose]);

  const { setContentRef, isDragging } = useBottomSheetDrag({
    onClose: handleClose,
    enabled: isOpen && !isClosing && !disableOpenAnimation,
  });

  useEffect(() => {
    if (isOpen) {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }
      setShouldRender(true);
      setIsClosing(false);
      openRafRef.current = requestAnimationFrame(() => {
        openRafRef.current = requestAnimationFrame(() => setIsVisible(true));
      });
    } else if (shouldRender && !isClosing) {
      setIsClosing(true);
      setIsVisible(false);
      closeTimerRef.current = setTimeout(() => {
        setShouldRender(false);
        setIsClosing(false);
      }, 150);
    }
    return () => {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }
      if (openRafRef.current) {
        cancelAnimationFrame(openRafRef.current);
        openRafRef.current = null;
      }
    };
  }, [isOpen]);

  if (!shouldRender) return null;

  const style: React.CSSProperties = {};
  if (height) style.height = height;
  if (minHeight) style.minHeight = minHeight;
  if (maxHeight) style.maxHeight = maxHeight;

  const bodyStyle: React.CSSProperties = {};
  if (bodyMaxHeight) bodyStyle.maxHeight = bodyMaxHeight;

  const handleGrabberClick = onGrabberClick ? () => triggerClose(onGrabberClick) : handleClose;
  const handleCloseClick = onCloseIcon ? () => triggerClose(onCloseIcon) : handleClose;

  return (
    <div
      className={`bottom-sheet-modal ${isVisible ? 'bottom-sheet-modal--open' : ''} ${isClosing ? 'bottom-sheet-modal--closing' : ''} ${isDragging ? 'bottom-sheet-modal--dragging' : ''} ${disableOpenAnimation ? 'bottom-sheet-modal--no-open-animation' : ''} ${className}`}
    >
      <Modal
        backdropEnabled={backdropEnabled}
        onClose={handleClose}
        closeIcon={false}
        fixedPosition
        disableBackdropClose={disableBackdropClose || disableOpenAnimation}
        onCloseIcon={onCloseIcon ? () => triggerClose(onCloseIcon) : undefined}
      >
        <div ref={setContentRef} className="bottom-sheet-modal__content" style={style}>
          {!disableOpenAnimation && (
            <div
              className="bottom-sheet-modal__grabber"
              onClick={handleGrabberClick}
              style={{
                cursor: onGrabberClick || onClose ? 'pointer' : 'default',
                touchAction: 'none',
              }}
            />
          )}

          {title || headerContent || (closeIcon && !disableOpenAnimation) ? (
            <div className="bottom-sheet-modal__header" style={{ touchAction: 'none' }}>
              {headerContent || (title && <h3 className="bottom-sheet-modal__title">{title}</h3>)}
              {closeIcon && !disableOpenAnimation && (
                <button
                  type="button"
                  onClick={handleCloseClick}
                  className="bottom-sheet-modal__close"
                  aria-label="Close"
                >
                  <IconX />
                </button>
              )}
            </div>
          ) : null}

          <div className="bottom-sheet-modal__body" style={bodyStyle}>
            {children}
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default BottomSheetModal;
